import { Link } from '../../components/SmartLink';
import {
  CheckCircle, Warning, MapPin, WhatsappLogo,
  Clock, ArrowRight, Shield, Laptop,
  Users, SealCheck, CalendarBlank,
  FileText, Star, PaintBrush, Code,
} from '@phosphor-icons/react';
import VisaDetailLayout from '../../components/visas/VisaDetailLayout';
import { VISA_BY_ID } from '../../data/visaData';
import { buildWAUrl } from '../../lib/whatsapp';
import { ExpandableTip, AIAnswerBox } from '../../components/visas/VisaPageHelpers';

const WA_URL = buildWAUrl(
  'Hi Big Bird Travel, I want to apply for a UAE Freelance Visa/permit. My field is ____. Please advise the best free-zone package and cost.'
);

const SUMMARY_CARDS = [
  { label: 'Permit + Residence', value: 'Combined Package', sub: 'Self-sponsored', Icon: Shield },
  { label: 'Validity', value: '1 – 5 Years', sub: 'Depends on free zone', Icon: CalendarBlank },
  { label: 'Processing Time', value: '1 – 3 Weeks', sub: 'Permit to visa stamping', Icon: Clock },
  { label: 'Who Can Apply', value: 'Consultants & Creatives', sub: 'Media, tech, design, coaching', Icon: Laptop },
  { label: 'Apply From', value: 'Abu Dhabi / Dubai', sub: 'Free-zone based', Icon: MapPin },
];

const GUIDE_FOR = [
  { Icon: PaintBrush, label: 'Designers, Photographers & Content Creators' },
  { Icon: Code,       label: 'Developers & IT Consultants' },
  { Icon: Users,      label: 'Marketing & Business Consultants' },
  { Icon: Laptop,     label: 'Remote Professionals Wanting UAE Residence' },
  { Icon: Star,       label: 'Coaches, Trainers & Educators' },
  { Icon: MapPin,     label: 'Abu Dhabi & Dubai-Based Freelancers' },
];

const PACKAGE_TABLE = [
  { field: 'Media & Content Creation', validity: 'Up to 2 years', requirement: 'Portfolio / CV', badge: 'blue' },
  { field: 'IT & Software Development', validity: 'Up to 2 years', requirement: 'Degree or experience proof', badge: 'blue' },
  { field: 'Consulting & Marketing', validity: 'Up to 2 years', requirement: 'CV + client references', badge: 'blue' },
  { field: 'Education & Training', validity: 'Up to 2 years', requirement: 'Qualification proof', badge: 'navy' },
];

const HIDDEN_FACTS = [
  {
    Icon: FileText,
    title: 'Free zones differ a lot in eligible activities',
    desc: 'Not every free zone covers every freelance field — some focus on media, others on tech or consulting. Picking the wrong one means your permit doesn’t actually cover what you do. We match your field to the right zone first.',
  },
  {
    Icon: Laptop,
    title: 'You can usually invoice both UAE and overseas clients',
    desc: 'Most freelance permits allow billing local and international clients within your approved activity — a key reason remote professionals choose this over a tourist visa for a longer UAE base.',
  },
  {
    Icon: Users,
    title: 'Family sponsorship becomes possible once you have the visa',
    desc: 'Once your freelance permit and residence visa are issued, you can generally sponsor your spouse and children like any other UAE resident, subject to the standard income and documentation rules.',
  },
  {
    Icon: Shield,
    title: 'An NOC matters if you’re currently employed in the UAE',
    desc: 'Switching from an employment visa to a freelance permit while staying in the UAE often requires a no-objection certificate from your current sponsor — we check this before starting your application.',
  },
];

const FREELANCE_SERVICES = [
  { label: 'Family Visa',           to: '/visas/family-visa',    desc: 'Sponsor spouse & children' },
  { label: 'Job Seeker Visa',       to: '/visas/job-seeker-visa', desc: 'If exploring employment first' },
  { label: 'Flights',               to: '/flights',              desc: 'Relocate to the UAE' },
  { label: 'Hotel Booking',         to: '/hotels',                desc: 'Temporary stays while settling in' },
  { label: 'All Visa Services',     to: '/visas',                 desc: 'UAE, Schengen, UK, USA & more' },
  { label: 'Contact Our Team',      to: '/contact',               desc: 'Mussafah, Abu Dhabi office' },
];

const SERVICE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'UAE Freelance Visa & Permit Assistance',
  description: 'UAE freelance visa and permit assistance — free-zone freelance permit combined with a UAE residence visa for consultants, media and tech professionals in Abu Dhabi and Dubai. Based in Mussafah, Abu Dhabi.',
  url: 'https://bigbirdtravel.com/visas/freelance-visa',
  provider: {
    '@type': 'TravelAgency',
    name: 'Big Bird Travel & Tourism',
    url: 'https://bigbirdtravel.com',
    telephone: '+971561789642',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Al Sawari Street 5, Building No-21, Office No-4, M-13',
      addressLocality: 'Mussafah',
      addressRegion: 'Abu Dhabi',
      addressCountry: 'AE',
    },
    email: 'info@bigbirdtravel.com',
    openingHours: 'Mo-Sa 09:00-18:00',
  },
  areaServed: { '@type': 'Country', name: 'United Arab Emirates' },
  serviceType: 'UAE Freelance Visa & Permit',
  termsOfService: 'https://bigbirdtravel.com/terms',
};

function FreelanceVisaExtraSections() {
  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(SERVICE_SCHEMA) }} />

      <section className="py-10 px-6 lg:px-8 bg-[#F5FAFF] border-b border-[#D8EAF7]">
        <div className="max-w-7xl mx-auto">
          <p className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-4">UAE Freelance Visa — At a Glance</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {SUMMARY_CARDS.map(({ label, value, sub, Icon }) => (
              <div key={label} className="rounded-2xl border border-[#C3DCF5] bg-white p-4 shadow-sm flex flex-col gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#EAF7FF] border border-[#C3DCF5]">
                  <Icon size={16} weight="fill" className="text-[#0068B3]" />
                </span>
                <p className="text-[10px] font-black uppercase tracking-wider text-[#0068B3]">{label}</p>
                <p className="font-display font-black text-[#071126] text-[14px] leading-tight">{value}</p>
                <p className="text-[11px] text-gray-400 leading-tight">{sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 px-6 lg:px-8 bg-white border-b border-gray-50">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-8 items-start">
          <div className="shrink-0 lg:w-60">
            <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-2 block">About This Guide</span>
            <h2 className="font-display font-black text-[#071126] text-[20px] leading-tight">Who this is for</h2>
            <p className="text-gray-500 text-[13px] mt-2 leading-relaxed">
              For independent professionals in Abu Dhabi and Dubai who want to freelance legally with their own UAE residence visa.
            </p>
          </div>
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {GUIDE_FOR.map(({ Icon, label }) => (
              <div key={label} className="flex items-center gap-3 rounded-xl border border-[#D8EAF7] bg-[#F5FAFF] px-4 py-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#EAF7FF] border border-[#C3DCF5]">
                  <Icon size={14} weight="fill" className="text-[#0068B3]" />
                </span>
                <span className="text-[13px] font-semibold text-[#071126]">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 space-y-4">
        <AIAnswerBox
          question="What is a UAE Freelance Visa?"
          answer="It is a combination of a free-zone freelance permit — which lets you legally invoice clients and work independently in an approved field such as media, tech, consulting, design or education — and a personal UAE residence visa, self-sponsored with no employer required. Big Bird Travel, based in Mussafah, Abu Dhabi, matches applicants to a suitable free-zone package based on their field and budget."
        />
        <AIAnswerBox
          question="Do I need a degree to get a UAE freelance permit?"
          answer="Many free-zone packages require a relevant bachelor's degree (attested) or several years of demonstrable experience in the applicant's field as an alternative. Requirements vary by free zone, so eligibility should be checked against the specific package before applying."
        />
      </div>

      <section className="py-12 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-3 block">Common Freelance Fields</span>
          <h2 className="font-display font-black text-[#071126] mb-2" style={{ fontSize: 'clamp(1.3rem, 2vw, 1.65rem)' }}>
            UAE freelance permit fields — 2026
          </h2>
          <p className="text-gray-500 text-[13px] mb-6 max-w-2xl">
            Exact eligible activities and fees vary by free zone — confirm your exact field and package with us.
          </p>
          <div className="overflow-x-auto rounded-2xl border border-[#D8EAF7] shadow-sm">
            <table className="w-full text-left text-[13px] min-w-[600px]">
              <thead>
                <tr className="bg-[#071126] text-white">
                  <th className="px-5 py-3.5 font-black text-[11px] uppercase tracking-wider rounded-tl-2xl">Field</th>
                  <th className="px-5 py-3.5 font-black text-[11px] uppercase tracking-wider">Typical Validity</th>
                  <th className="px-5 py-3.5 font-black text-[11px] uppercase tracking-wider rounded-tr-2xl">Key Requirement</th>
                </tr>
              </thead>
              <tbody>
                {PACKAGE_TABLE.map((row, i) => (
                  <tr key={row.field} className={`border-t border-[#E8F3FC] ${i % 2 === 0 ? 'bg-white' : 'bg-[#F5FAFF]'}`}>
                    <td className="px-5 py-3.5 font-semibold text-[#071126]">{row.field}</td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-block rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wide whitespace-nowrap ${
                        row.badge === 'navy' ? 'bg-[#071126] text-white' : 'bg-[#EAF7FF] text-[#0068B3]'
                      }`}>{row.validity}</span>
                    </td>
                    <td className="px-5 py-3.5 text-gray-600">{row.requirement}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="py-14 px-6 lg:px-8 bg-amber-50 border-y border-amber-100">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
          <div className="lg:col-span-2">
            <span className="text-[11px] font-black uppercase tracking-[0.3em] text-amber-700 mb-3 block">⚠ Know Before You Apply</span>
            <h2 className="font-display font-black text-[#071126] mb-4" style={{ fontSize: 'clamp(1.4rem, 2.2vw, 1.85rem)' }}>
              Choosing the right free zone matters more than the price
            </h2>
            <p className="text-gray-700 text-[15px] leading-relaxed mb-4">
              The cheapest package isn't useful if it doesn't cover your actual field of work. Matching your profession to the right free zone's activity list is the first real decision.
            </p>
            <div className="space-y-2.5 mt-6">
              <ExpandableTip title="Confirm your field is covered before paying">
                Free zones publish specific lists of eligible freelance activities. We check your exact field against current lists so your permit actually covers your work.
              </ExpandableTip>
              <ExpandableTip title="Get an NOC sorted if currently employed">
                If you hold a UAE employment visa and want to switch, a no-objection certificate from your current employer is often required. Start this conversation early.
              </ExpandableTip>
              <ExpandableTip title="Keep your portfolio ready and current">
                A clear CV or portfolio showing relevant freelance work speeds up approval — especially for media, design and consulting fields where evidence of work matters more than formal degrees.
              </ExpandableTip>
              <ExpandableTip title="Plan for the residence visa steps too">
                The freelance permit is only half the process — medical test, Emirates ID and visa stamping follow. We manage the full sequence so nothing is missed.
              </ExpandableTip>
            </div>
          </div>

          <div className="bg-white border border-amber-200 rounded-2xl p-6 shadow-sm lg:sticky lg:top-24">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle size={18} weight="fill" className="text-[#0068B3]" />
              <span className="font-black text-[12px] uppercase tracking-widest text-[#0068B3]">Quick Eligibility Check</span>
            </div>
            <ul className="space-y-3 mb-5">
              {[
                { text: 'Media, design or content creation', result: 'Covered by most zones', type: 'ok' },
                { text: 'IT, tech or consulting', result: 'Covered by most zones', type: 'ok' },
                { text: 'Currently on employment visa', result: 'NOC may be needed', type: 'warn' },
                { text: 'No degree, but 5+ yrs experience', result: 'Often accepted as alternative', type: 'ok' },
              ].map(item => (
                <li key={item.text} className="flex items-start gap-2.5">
                  {item.type === 'ok' && <CheckCircle size={14} weight="fill" className="text-green-500 shrink-0 mt-0.5" />}
                  {item.type === 'warn' && <Warning size={14} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />}
                  <span className="text-[12px] text-gray-700"><strong>{item.text}</strong> → {item.result}</span>
                </li>
              ))}
            </ul>
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full rounded-xl bg-[#0F7A35] text-white text-[13px] font-black py-3.5 hover:bg-[#0a6029] transition-colors">
              <WhatsappLogo size={17} weight="fill" /> Check My Eligibility — Free
            </a>
            <p className="text-center text-[11px] text-gray-400 mt-2.5 leading-snug">
              Send your field & CV summary — honest answer, same day.
            </p>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden py-14 px-6 lg:px-8 bg-[#071126] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(0,150,255,0.10),transparent_55%)] pointer-events-none" />
        <div className="relative z-10 max-w-7xl mx-auto">
          <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#C9A227] mb-3 block">Authority Section</span>
          <h2 className="font-display font-black text-white mb-3" style={{ fontSize: 'clamp(1.4rem, 2.2vw, 1.85rem)' }}>
            What most freelance visa pages don't tell you
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {HIDDEN_FACTS.map(({ Icon, title, desc }) => (
              <div key={title} className="rounded-2xl border border-[#0096FF]/20 bg-white/[0.045] p-5 hover:border-[#0096FF]/40 hover:bg-white/[0.06] transition-all">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0096FF]/10 border border-[#0096FF]/20 mb-4">
                  <Icon size={17} weight="fill" className="text-[#0096FF]" />
                </span>
                <h3 className="font-display font-black text-white text-[13px] mb-2 leading-tight">{title}</h3>
                <p className="text-blue-50/60 text-[12px] leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 rounded-2xl border border-[#0096FF]/25 bg-white/[0.04] p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1">
              <p className="font-display font-black text-white text-[15px] mb-1">Ready to freelance legally in the UAE?</p>
              <p className="text-blue-50/60 text-[13px]">Send your field of work and CV summary. We'll confirm the right free-zone package and an honest cost — usually within the hour.</p>
            </div>
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="shrink-0 inline-flex items-center gap-2 bg-[#0F7A35] text-white text-[13px] font-black px-5 py-3 rounded-xl hover:bg-[#0a6029] transition-colors">
              <WhatsappLogo size={17} weight="fill" /> Ask on WhatsApp
            </a>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden py-14 px-6 lg:px-8 bg-[#041024] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(0,150,255,0.14),transparent_50%)]" />
        <div className="relative z-10 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0096FF] mb-3 block">Why Big Bird</span>
            <h2 className="font-display font-black text-white mb-4" style={{ fontSize: 'clamp(1.4rem, 2.2vw, 1.85rem)' }}>
              Freelance permit + residence, handled together
            </h2>
            <p className="text-blue-50/85 text-[15px] italic leading-relaxed mb-5 border-l-2 border-[#0096FF] pl-4">
              "Send your field and CV. We'll match you to the right free-zone package and manage the permit and residence visa from start to finish."
            </p>
            <p className="text-blue-50/70 text-[14px] leading-relaxed mb-4">
              We're a licensed travel agency in <strong className="text-white">Mussafah, Abu Dhabi</strong>. For freelance visas we:
            </p>
            <ul className="space-y-3 mb-6">
              {[
                'Match your field to the right free-zone package.',
                'Prepare your CV/portfolio evidence for submission.',
                'Handle NOC coordination if you are switching visa status.',
                'Manage the medical test, Emirates ID and visa stamping.',
                'Advise on family sponsorship once your visa is issued.',
              ].map(item => (
                <li key={item} className="flex items-start gap-3 text-[13px] text-blue-50/80">
                  <CheckCircle size={14} weight="fill" className="text-[#0096FF] shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#0F7A35] text-white text-[13px] font-black px-6 py-3.5 rounded-xl hover:bg-[#0a6029] transition-colors">
              <WhatsappLogo size={19} weight="fill" /> WhatsApp Us — Same-Day Response
            </a>
          </div>
          <div className="space-y-4">
            <div className="bg-white/[0.05] border border-[#0096FF]/20 rounded-3xl p-6">
              <p className="text-[10px] font-black uppercase tracking-widest text-[#0096FF]/70 mb-4">Big Bird Travel — Agency Details</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                {[
                  { label: 'Office',   value: 'Mussafah 13, Abu Dhabi' },
                  { label: 'WhatsApp', value: '+971 56 178 9642' },
                  { label: 'License',  value: 'ADRA CN-5285999' },
                  { label: 'Response', value: 'Same day' },
                  { label: 'Hours',    value: 'Mon–Sat 9am–6pm UAE' },
                  { label: 'Serves',   value: 'Abu Dhabi · Dubai · UAE' },
                ].map(item => (
                  <div key={item.label}>
                    <p className="text-[10px] font-black uppercase tracking-widest text-[#0096FF]/60 mb-0.5">{item.label}</p>
                    <p className="text-[13px] font-semibold text-white">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white/[0.05] border border-[#0096FF]/20 rounded-2xl p-5">
              <div className="flex items-center gap-1 mb-2">
                {[...Array(5)].map((_, i) => (<Star key={i} size={12} weight="fill" className="text-[#C9A227]" />))}
                <span className="text-[11px] text-blue-50/50 ml-2">Verified customer</span>
              </div>
              <blockquote className="text-[13px] text-blue-50/80 italic leading-relaxed">
                "I wasn't sure which free zone covered graphic design freelancing. They matched me to the right one and I was legally set up within three weeks."
              </blockquote>
              <p className="text-[11px] text-blue-50/40 mt-2">— Freelance designer, Dubai</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 px-6 lg:px-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto">
          <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-3 block">Related Visa Services</span>
          <h2 className="font-display font-black text-[#071126] mb-6 text-[20px]">
            Freelance visa sorted. Here's what else to plan.
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {FREELANCE_SERVICES.map(({ label, to, desc }) => (
              <Link key={label} to={to} className="group rounded-2xl border border-[#D8EAF7] bg-[#F5FAFF] p-4 hover:border-[#0096FF]/50 hover:bg-[#EAF7FF] hover:-translate-y-0.5 transition-all flex flex-col gap-2">
                <ArrowRight size={15} weight="bold" className="text-[#0068B3] group-hover:translate-x-0.5 transition-transform" />
                <p className="font-display font-black text-[#071126] text-[13px] leading-tight">{label}</p>
                <p className="text-[11px] text-gray-400">{desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-7 px-6 lg:px-8 bg-[#F5FAFF] border-b border-[#D8EAF7]">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-x-8 gap-y-3">
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><CalendarBlank size={13} weight="fill" className="text-[#0068B3]" /><span><strong className="text-[#071126]">Last Updated:</strong> September 2026</span></div>
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><SealCheck size={13} weight="fill" className="text-[#0068B3]" /><span><strong className="text-[#071126]">Reviewed by:</strong> Big Bird Travel Visa Team</span></div>
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><Shield size={13} weight="fill" className="text-[#0068B3]" /><span><strong className="text-[#071126]">License:</strong> ADRA CN-5285999</span></div>
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><MapPin size={13} weight="fill" className="text-[#0068B3]" /><span><strong className="text-[#071126]">Office:</strong> Mussafah 13, Abu Dhabi, UAE</span></div>
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><WhatsappLogo size={13} weight="fill" className="text-green-600" /><span><strong className="text-[#071126]">WhatsApp:</strong> Same-day response</span></div>
        </div>
      </section>

      <section className="py-12 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-6 items-start">
            <div className="shrink-0">
              <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-2 block">Local Service</span>
              <h2 className="font-display font-black text-[#071126] text-[18px]">Areas We Serve</h2>
            </div>
            <div className="flex-1">
              <p className="text-gray-500 text-[14px] mb-4">
                We assist freelance visa applicants across <strong className="text-[#071126]">Abu Dhabi, Dubai, and the Northern Emirates</strong> — from our Mussafah office.
              </p>
              <div className="flex flex-wrap gap-2">
                {['Mussafah', 'Mohammed Bin Zayed City', 'Khalifa City', 'Al Nahyan', 'Abu Dhabi City', 'Al Ain', 'Dubai', 'Sharjah', 'Northern Emirates'].map(area => (
                  <span key={area} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#EAF7FF] border border-[#C3DCF5] text-[12px] font-black text-[#0068B3]">
                    <MapPin size={11} weight="fill" />{area}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default function FreelanceVisa() {
  return (
    <VisaDetailLayout
      visa={VISA_BY_ID['freelance-visa']}
      extraSections={<FreelanceVisaExtraSections />}
    />
  );
}
